import { useCallback } from "react";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { listAIConversationsPaginated, patchAIConversation, deleteAIConversation } from "@/lib/ai-chat/client";
import type { ConversationSummary } from "@/lib/ai-chat/types";
import { queryKeys } from "@/lib/query-keys";

const AI_CONVERSATIONS_KEY = queryKeys.aiConversations.all();

interface InfiniteConversationsData {
  pages: Array<{
    conversations: ConversationSummary[];
    nextCursor: string | null;
    hasNextPage: boolean;
  }>;
  pageParams: Array<string | null>;
}

export const useAIConversations = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const queryClient = useQueryClient();

  const { data: infiniteData, isLoading: loading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: AI_CONVERSATIONS_KEY,
    enabled,
    queryFn: async ({ pageParam }) => {
      return await listAIConversationsPaginated(pageParam);
    },
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.hasNextPage ? lastPage.nextCursor : undefined,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
  });

  const conversations = infiniteData?.pages.flatMap((page) => page.conversations) ?? [];

  const refreshConversations = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: AI_CONVERSATIONS_KEY });
  }, [queryClient]);

  const editConversation = useCallback(async (id: string, newTitle: string) => {
    await patchAIConversation(id, { title: newTitle });
    
    queryClient.setQueriesData<InfiniteConversationsData>(
      { queryKey: AI_CONVERSATIONS_KEY },
      (old) => {
        if (!old) return old;
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            conversations: page.conversations.map((c) =>
              c.id === id ? { ...c, title: newTitle } : c
            ),
          })),
        };
      }
    );
  }, [queryClient]);

  const removeConversation = useCallback(async (id: string) => {
    await deleteAIConversation(id);
    
    queryClient.setQueriesData<InfiniteConversationsData>(
      { queryKey: AI_CONVERSATIONS_KEY },
      (old) => {
        if (!old) return old;
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            conversations: page.conversations.filter((c) => c.id !== id),
          })),
        };
      }
    );
  }, [queryClient]);

  return {
    conversations,
    loading,
    refreshConversations,
    editConversation,
    removeConversation,
    fetchNextPage,
    hasNextPage: hasNextPage ?? false,
    isFetchingNextPage,
  };
};
